<template>
  <div class="color-picker-container-outer">
    <span class="color-picker-container-inner">
      <span class="picker-icon" :class="{ 'is-light': isLight }">
        <i class="fas fa-fw fa-fill-drip"></i>
      </span>
      <span class="color-picker" ref="colorPickerWrapperElement"></span>
    </span>
    <label v-if="formattedLabelText" class="color-picker-label">{{ formattedLabelText }}</label>
  </div>
</template>

<script lang="ts" setup>
  import { ref, toRefs, computed, watch, onMounted, onUnmounted, defineExpose, Ref } from 'vue';
  import chroma from 'chroma-js';
  import Pickr from '@simonwep/pickr';
  import { isValidHexCode } from '@/utils/isValidHexCode';
  import { throttle } from '@/utils/throttle';

  export interface ColorPickerExposed {
    colorPickerRootElement: HTMLElement | undefined;
  }

  type ColorPickerExposedWrapper = {
    [key in keyof ColorPickerExposed]: Ref<ColorPickerExposed[key]> | ColorPickerExposed[key];
  }

  const colorPickerWrapperElement = ref<HTMLElement>();
  const colorPickerRootElement = ref<HTMLElement>();
  let colorPicker: Pickr | null = null;
  const isLight = ref(false);
  let dontUpdateColor = false;
  const DEFAULT_COLOR = '#000000';
  const DEFAULT_SWATCHES = [
    '#000000', '#626262', '#8E1F1E', '#E79747', '#8FD528', '#4DACE7', '#5F338A',
    '#FFFFFF', '#CCCCCC', '#D73737', '#F7E82C', '#35841C', '#024ab8', '#F3359C'
  ];

  defineExpose<ColorPickerExposedWrapper>({ colorPickerRootElement });

  const props = defineProps<{
    color: string;
    defaultColor: string;
    labelText: string;
  }>();
  const { color, defaultColor, labelText } = toRefs(props);

  const emit = defineEmits<{
    (e: 'update:color', color: string): void;
  }>();

  const colorHexCode = computed(() => {
    return convertToHexCode(color.value, defaultColor.value);
  });

  const defaultColorHexCode = computed(() => {
    return convertToHexCode(defaultColor.value, DEFAULT_COLOR);
  });

  const formattedLabelText = computed(() => {
    return typeof labelText.value === 'string' ? labelText.value.trim() : '';
  });

  function handleColorPickerInit() {
    if (!colorPicker || typeof colorPicker.getColor !== 'function') {
      return;
    }

    const colorObj = colorPicker.getColor();
    const previousDontUpdateColorValue = dontUpdateColor;
    dontUpdateColor = true;

    handleColorPickerChanged(colorObj);

    dontUpdateColor = previousDontUpdateColorValue;
  }

  function handleColorPickerClear() {
    if (!colorPicker || typeof colorPicker.setColor !== 'function') {
      return;
    }

    emitColorUpdate(defaultColorHexCode.value);

    dontUpdateColor = true;

    colorPicker.setColor(defaultColorHexCode.value, true);

    window.setTimeout(() => {
      dontUpdateColor = false;
    }, 0);
  }

  function handleColorPickerChanged(colorObj: Pickr.HSVaColor) {
    if (!colorObj || typeof colorObj.toHEXA !== 'function') {
      isLight.value = true;
      return;
    }

    const currentColor = colorObj.toHEXA();

    if (typeof currentColor.toString !== 'function') {
      isLight.value = true;
      return;
    }

    let currentColorString = currentColor.toString();

    if (typeof currentColorString !== 'string') {
      isLight.value = true;
      return;
    }

    currentColorString = currentColorString.trim().toUpperCase().slice(0, 7);
    const chromaObj = chroma(currentColorString);

    if (!chromaObj || typeof chromaObj.luminance !== 'function') {
      isLight.value = true;
      return;
    }

    isLight.value = chromaObj.luminance() > 0.4;

    if (dontUpdateColor || !isValidHexCode(currentColorString)) {
      return;
    }

    emitColorUpdate(currentColorString);
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (
      e.key !== 'Enter' ||
      !colorPicker ||
      typeof colorPicker.hide !== 'function'
    ) {
      return;
    }

    colorPicker.hide();
  }

  function convertToHexCode(colorString: string, defaultColor: string) {
    if (typeof colorString !== 'string') {
      return convertToHexCode(defaultColor, DEFAULT_COLOR);
    }

    colorString = colorString.trim().toUpperCase().slice(0, 7);

    if (
      !colorString ||
      colorString === 'AUTO' ||
      colorString === 'DEFAULT'
    ) {
      return convertToHexCode(defaultColor, DEFAULT_COLOR);
    }

    if (isValidHexCode(colorString)) {
      return colorString;
    }

    const hexCode = chroma(colorString).hex();

    if (isValidHexCode(hexCode)) {
      return hexCode.trim().toUpperCase();
    }

    return convertToHexCode(defaultColor, DEFAULT_COLOR);
  }

  function handleColorHexCodeChanged() {
    if (
      !colorPicker ||
      typeof colorPicker.setColor !== 'function'
    ) {
      return;
    }

    dontUpdateColor = true;

    colorPicker.setColor(colorHexCode.value, true);

    window.setTimeout(() => {
      dontUpdateColor = false;
    }, 0);
  }

  const emitColorUpdate = throttle((newColor: string) => {
    emit('update:color', newColor)
  }, 1000, true);

  watch(colorHexCode, handleColorHexCodeChanged);

  onMounted(() => {
    if (!colorPickerWrapperElement.value) {
      window.sc3WorkbenchLogger.error('Color picker wrapper element was null.');
      return;
    }

    const pickrConfig: Pickr.Options = {
      el: colorPickerWrapperElement.value,
      theme: 'nano', // or 'monolith', or 'classic'
      padding: 5,
      closeWithKey: 'Escape',
      default: colorHexCode.value,
      comparison: false,
      position: 'bottom-start',
      swatches: DEFAULT_SWATCHES,
      components: {
        // Main components
        preview: true,
        opacity: false,
        hue: true,
        // Input / output Options
        interaction: {
          hex: false,
          rgba: false,
          hsla: false,
          hsva: false,
          cmyk: false,
          input: true,
          clear: true,
          cancel: false,
          save: false
        },
      },
      i18n: {
        'btn:clear': 'Reset',
        'btn:save': '&starf;'
      }
    };

    colorPicker = Pickr.create(pickrConfig);

    colorPicker.on('init', handleColorPickerInit);
    colorPicker.on('change', handleColorPickerChanged);
    colorPicker.on('clear', handleColorPickerClear);
    document.body.addEventListener('keydown', handleKeyDown);

    const root = colorPicker.getRoot();
    if ('app' in root && root.app instanceof HTMLElement) {
      colorPickerRootElement.value = root.app;
    }

    onUnmounted(() => {
      document.body.removeEventListener('keydown', handleKeyDown);
    });
  });
</script>

<style>
  div.color-picker-container-outer {
    margin: 6px;
    display: flex !important;
    align-items: center;
  }

  .color-picker-container-inner {
    position: relative;
    display: inline-block;
  }

  .color-picker-label {
    display: inline-block;
    margin: 0px 0px 0px 4px;
    padding: 0px;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 700;
  }

  .picker-icon {
    position: absolute !important;
    z-index: 1;
    left: 8px;
    top: 8px;
    font-size: 14px;
    color: white;
    pointer-events: none;
    box-shadow: none;
    outline: none;
  }

  .is-light {
    color: black;
  }

  .pickr {
    display: inline-block;
    font-size: 0px;
  }

  .pickr .pcr-button {
    border: solid 2px #a9b2bc;
    border-radius: 4px;
    margin: 0px;
    font-size: 16px;
  }

  .color-picker,
  .pickr button:focus {
    box-shadow: none;
    outline: none;
  }

  .pcr-app {
    font-size: 16px;
    font-weight: 400;
    color: #212529;
    user-select: none;
    text-size-adjust: 100%;
  }

  .pcr-app .pcr-swatches > button {
    outline: none;
  }

  .pcr-app .pcr-interaction .pcr-cancel,
  .pcr-app .pcr-interaction .pcr-clear {
    background-color: #707f8f;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 700;
  }

  .pcr-app .pcr-swatches {
    margin-top: 35px !important;
    margin-bottom: 5px;
  }

  .pcr-app .pcr-interaction {
    top: 165px;
    position: absolute;
  }

  .pcr-app input {
    height: unset;
  }

  .pcr-app .pcr-interaction input[type=text],
  .pcr-app .pcr-interaction .pcr-save {
    border: none;
    font-weight: 300;
    font-family: unset;
  }

  .pcr-app .pcr-interaction .pcr-result {
    width: 193px;
  }

  .pcr-app .pcr-interaction .pcr-save {
    background: #707f8f;
    box-shadow: none !important;
    outline: none !important;
  }
</style>
